import { useCallback, useRef, useState } from "react";
import type { TouchEvent } from "react";

/** How far the row can be dragged — the width of the action revealed behind it. */
export const SWIPE_ACTION_WIDTH = 112;
/** Past this much drag, releasing commits the action. */
export const SWIPE_TRIGGER_DISTANCE = 72;
/** Movement before the gesture commits to an axis, so a tap never drags. */
const AXIS_LOCK_SLOP = 8;

type Axis = "unknown" | "horizontal" | "vertical";

interface UseSwipeActionOptions {
  /** Fired once, on release, when the drag passed the trigger distance. */
  onTrigger: () => void;
  /** When false the row never moves — desktop, selection mode, no permission. */
  enabled?: boolean;
}

interface SwipeActionHandlers {
  onTouchStart: (event: TouchEvent) => void;
  onTouchMove: (event: TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

/**
 * Drag-left-to-reveal-an-action on a list row — the photos list's "Raise
 * issue" ([[photo-list-item.spec]] US-005) and the issues list's "Update
 * status" ([[issue-item.spec]] US-001). One hook so the two lists keep the
 * same thresholds and feel.
 *
 * Touch only: the gesture is for thumbs, and binding it to touch events alone
 * keeps a desktop mouse out of it entirely. The drag locks to an axis after a
 * few pixels — a vertical-dominant move is handed back to the scroller and
 * ignored for the rest of that gesture, so scrolling a list of rows never
 * peels one open.
 *
 * Only leftward movement counts; dragging right does nothing. Releasing past
 * {@link SWIPE_TRIGGER_DISTANCE} fires `onTrigger` and the row springs back —
 * whatever the action opens is the confirmation, so leaving the row hanging
 * open would just be a second thing to dismiss.
 */
export function useSwipeAction({
  onTrigger,
  enabled = true,
}: UseSwipeActionOptions): {
  /** Current drag, in px, from 0 to -SWIPE_ACTION_WIDTH. */
  offset: number;
  /** True while the finger is down and dragging horizontally — the row
   * follows the finger with no transition, and springs back with one. */
  isDragging: boolean;
  handlers: SwipeActionHandlers;
  /** True (once) when the click now arriving is the tail of a swipe, so the
   * row can ignore it instead of also opening the record. */
  consumeClick: () => boolean;
} {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const axisRef = useRef<Axis>("unknown");
  const didSwipeRef = useRef(false);
  // The live drag, for the release to judge. `offset` state is what the last
  // render painted, and a finger can lift before React renders its final move
  // — touchend is dispatched at once, touchmove's update a task later — so a
  // flick that crossed the trigger on that move would read as short of it.
  const offsetRef = useRef(0);

  const reset = useCallback(() => {
    startRef.current = null;
    axisRef.current = "unknown";
    offsetRef.current = 0;
    setIsDragging(false);
    setOffset(0);
  }, []);

  const onTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!enabled) return;
      // Multi-touch is a pinch, not a swipe.
      if (event.touches.length >= 2) {
        reset();
        return;
      }
      const touch = event.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      axisRef.current = "unknown";
      // A new gesture. Any click owed to the last swipe has either arrived or
      // never will — a real phone sends none after a drag — so a flag still
      // set here would swallow this tap instead.
      didSwipeRef.current = false;
    },
    [enabled, reset],
  );

  const onTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!enabled) return;
      const start = startRef.current;
      if (!start) return;
      if (event.touches.length >= 2) {
        reset();
        return;
      }
      const touch = event.touches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;

      if (axisRef.current === "unknown") {
        if (Math.abs(dy) > AXIS_LOCK_SLOP && Math.abs(dy) >= Math.abs(dx)) {
          // Scrolling. Stay out of the way for the rest of this gesture.
          axisRef.current = "vertical";
          return;
        }
        if (Math.abs(dx) > AXIS_LOCK_SLOP) {
          axisRef.current = "horizontal";
          didSwipeRef.current = true;
          setIsDragging(true);
        } else {
          return;
        }
      }
      if (axisRef.current !== "horizontal") return;

      // Leftward only, and never past the width of the action behind it.
      offsetRef.current = Math.max(-SWIPE_ACTION_WIDTH, Math.min(0, dx));
      setOffset(offsetRef.current);
    },
    [enabled, reset],
  );

  const onTouchEnd = useCallback(() => {
    const committed =
      axisRef.current === "horizontal" &&
      offsetRef.current <= -SWIPE_TRIGGER_DISTANCE;
    reset();
    if (committed) onTrigger();
  }, [onTrigger, reset]);

  const consumeClick = useCallback(() => {
    if (!didSwipeRef.current) return false;
    didSwipeRef.current = false;
    return true;
  }, []);

  return {
    offset,
    isDragging,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: reset,
    },
    consumeClick,
  };
}
